import { Bell, FileSpreadsheet } from "lucide-react";
import { TbWorldDown } from "react-icons/tb";
import { Users } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "../ui/separator";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import useAuth from "@/hooks/useAuth";
import toast from "react-hot-toast";
import {
  getNotificationsApi,
  updateNotificationsReadStatusApi,
} from "@/api/notifications";
import { orderAccept, orderCancel } from "@/api/online-ordering/website-ordering/order-request";
import PusherHandler from "@/api/lib/pusher/PusherHandler";
import {
  editReservation,
  acceptReservationThunk,
  rejectReservationThunk,
  clearReservationMessages,
  updateReservationState
} from "@/redux/slice/reservation-slice/reservation-slice";
import { useSelector, useDispatch } from "react-redux";
import ReservationRejectDialog from "@/components/reservation/reservation-reject-modal";
import { acceptReservation, getReservationById } from '@/api/new-api/reservations/reservation'
import { notificationSound } from "@/assets";
import {
  getOrderDetails,
} from '@/api/online-ordering/website-ordering/order-request'
import { addNewOnlineOrder, replaceOnlineOrder } from "@/redux/slice/order-ordering-slice/order-status-slice";


const skeletonRows = Array.from({ length: 5 });

const getLondonTimeParts = (dateInput) => {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(dateInput);
  const year = parts.find((p) => p.type === "year").value;
  const month = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  const hour = parts.find((p) => p.type === "hour").value;
  const minute = parts.find((p) => p.type === "minute").value;
  const second = parts.find((p) => p.type === "second").value;
  return new Date(year, month - 1, day, hour, minute, second);
};

const parseAsLondonTime = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    return getLondonTimeParts(dateValue);
  }
  const str = String(dateValue).trim();
  const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(str);
  if (hasTimezone) {
    const parsedDate = new Date(str);
    if (Number.isNaN(parsedDate.getTime())) return null;
    return getLondonTimeParts(parsedDate);
  } else {
    const normalizedStr = str.replace("T", " ");
    const match = normalizedStr.match(/^(\d{4})[-/](\d{2})[-/](\d{2}) (\d{2}):(\d{2}):(\d{2})/);
    if (match) {
      const [_, year, month, day, hour, minute, second] = match;
      return new Date(year, month - 1, day, hour, minute, second);
    } else {
      const parsedDate = new Date(str);
      if (Number.isNaN(parsedDate.getTime())) return null;
      return parsedDate;
    }
  }
};

const formatTimeAgo = (dateValue) => {
  if (!dateValue) return "";
  const eventDate = parseAsLondonTime(dateValue);
  if (!eventDate) return "";

  const nowLondon = getLondonTimeParts(new Date());
  const diff = nowLondon.getTime() - eventDate.getTime();

  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} d ago`;
};

function OnlineOrderNotification() {
  const { user } = useAuth();
  const restaurant_id = user?.restaurant_id;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const notificationsContainerRef = useRef(null);
  const autoMarkTimeoutRef = useRef(null);
  const dispatch = useDispatch();
  const [orderConfirmModal, setOrderConfirmModal] = useState({
    isOpen: false,
    action: null, // 'accept' or 'reject'
    orderId: null,
    orderName: null,
    isLoading: false,
  });
  const [reservationConfirmModal, setReservationConfirmModal] = useState({
    isOpen: false,
    action: null, // 'accept' or 'reject'
    reservationId: null,
    reservationName: null,
    isLoading: false,
  });

  const { notificationSound: isNotificationSoundEnabled = true, volume } = useSelector(
      (state) => state.utility.soundNotification || {},
  );
  const isNotificationSoundEnabledRef = useRef(isNotificationSoundEnabled);
  const volumeRef = useRef(volume);

  useEffect(() => {
    isNotificationSoundEnabledRef.current = isNotificationSoundEnabled;
  }, [isNotificationSoundEnabled]);

  useEffect(() => {
    volumeRef.current = volume;
  }, [volume]);

  const incomingAudioRef = useRef(null);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item?.is_read).length,
    [notifications]
  );

  const {
      connectionStatus: tableStatus,
      lastUpdate: lastTableUpdate,
      addEventHandler,
      isConnected: tableConnected
  } = PusherHandler();

  const stopIncomingSound = useCallback(() => {
      const currentAudio = incomingAudioRef.current;
      if (!currentAudio) return;

      currentAudio.pause();
      currentAudio.currentTime = 0;
      incomingAudioRef.current = null;
  }, []);

  const playIncomingSound = useCallback(() => {
      stopIncomingSound();

      console.log("ONLINE playIncomingSound: isNotificationSoundEnabledRef.current =", isNotificationSoundEnabledRef.current);
      if (!isNotificationSoundEnabledRef.current) return;

      const audio = new Audio(notificationSound);
      audio.volume = typeof volumeRef.current === "number" ? volumeRef.current : 1;
      audio.play().catch(() => {});
      incomingAudioRef.current = audio;
  }, [stopIncomingSound]);

  useEffect(() => {
    if (!open || !restaurant_id) return;
    let isActive = true;

    const loadNotifications = async (pageNum = 1) => {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await getNotificationsApi(restaurant_id, {
        per_page: 5,
        page: pageNum,
        category : "online_notification", 
      });

      console.log("Notifications response:", response);

      const list =
        response?.data?.notifications || [];
      const totalUnread = response?.data?.unread_count || 0;
      const pagination = response?.data?.pagination || response?.pagination || {};

      if (!isActive) return;

      // Append notifications if loading more, replace if first page
      if (pageNum === 1) {
        setNotifications(Array.isArray(list) ? list : []);
      } else {
        setNotifications((prev) => [...prev, ...list]);
      }

      setTotalUnread(totalUnread);

      // Check if there are more pages
      const currentPage = pagination.current_page || pagination.currentPage || pageNum;
      const lastPage = pagination.last_page || pagination.lastPage || 1;
      setHasMore(currentPage < lastPage);

      if (pageNum === 1) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }

      const unreadIds = (Array.isArray(list) ? list : [])
        .filter((item) => item && item.is_read === false)
        .map((item) => item.id)
        .filter(Boolean);

      if (unreadIds.length && pageNum === 1) {
        await updateNotificationsReadStatusApi(restaurant_id, {
          ids: unreadIds,
          is_read: true,
        });
        if (!isActive) return;
        setNotifications((prev) =>
          prev.map((item) =>
            unreadIds.includes(item.id) ? { ...item, is_read: true } : item
          )
        );
      }
    };

    setPage(1);
    loadNotifications(1);
    return () => {
      isActive = false;
    };
  }, [open, restaurant_id]);

  const handleShowConfirm = (action, orderId, orderName, type) => {
    if (type === "order") {
    setOrderConfirmModal({
      isOpen: true,
      action,
      orderId,
      orderName,
      isLoading: false,
    });
    } else if (type === "reservation") {
      setReservationConfirmModal({
        isOpen: true,
        action,
        reservationId: orderId,
        reservationName: orderName,
        isLoading: false,
      });
    }
  };



  // For Order actions
  const handleConfirmAcceptOrderAction = async () => {
    const { action, orderId } = orderConfirmModal;
    setOrderConfirmModal((prev) => ({ ...prev, isLoading: true }));

    try {
      let response;
      if (action === "accept") {
        response = await orderAccept(orderId);
      } else if (action === "reject") {
        response = await orderCancel(orderId);
      }

      if (response?.status === "success") {
        toast.success(
          action === "accept"
            ? "Order accepted successfully"
            : "Order rejected successfully"
        );
        // Remove the notification from list
        setNotifications((prev) =>
          prev.filter((item) => item?.data?.online_order_id !== orderId)
        );
      } else {
        toast.error(
          response?.message ||
            `Failed to ${action} order`
        );
      }
    } catch (error) {
      console.error(`Error ${orderConfirmModal.action}ing order:`, error);
      toast.error(`Failed to ${orderConfirmModal.action} order`);
    } finally {
      setOrderConfirmModal({
        isOpen: false,
        action: null,
        orderId: null,
        orderName: null,
        isLoading: false,
      });
    }
  };

  const handleOrderCancelAction = () => {
    setOrderConfirmModal({
      isOpen: false,
      action: null,
      orderId: null,
      orderName: null,
      isLoading: false,
    });
  };

  const handleConfirmAcceptReservationAction = async () => {
    setReservationConfirmModal((prev) => ({ ...prev, isLoading: true }));

      console.log("Reservation confirm modal data:", reservationConfirmModal);
      acceptReservation(reservationConfirmModal.reservationId).then((response) => {
        if (response?.status === "success") {
          toast.success("Reservation accepted successfully");
          setReservationConfirmModal({
            isOpen: false,
            action: null,
            reservationId: null,
            reservationName: null,
            isLoading: false,
          });
        }
        else {          
          toast.error(response?.message || "Failed to accept reservation");
          setReservationConfirmModal((prev) => ({ ...prev, isLoading: false }));
        }
      }).catch((error) => {        console.error("Error accepting reservation:", error);
        toast.error("Failed to accept reservation");
        setReservationConfirmModal((prev) => ({ ...prev, isLoading: false }));
      });
    }
      


  const handleReservationCancelAction = () => {
    setReservationConfirmModal({
      isOpen: false,
      action: null,
      reservationId: null,
      reservationName: null,
      isLoading: false,
    });
  };  
  
  
  function addOnlineOrdertoRequestList(orderId) {
    if(window.location.pathname.includes("/onlineordering/website-ordering/order-request")) {
      getOrderDetails(orderId).then((response) => {
        if (response?.status === "success") {
          dispatch(addNewOnlineOrder(response.data));
        } else {
          console.error("Failed to fetch order details:", response?.message);
        }
      }).catch((error) => {
        console.error("Error fetching order details:", error);
      });
    }
  }

  function updateOnlineOrderInRequestList(orderId) {
    if(window.location.pathname.includes("/onlineordering/website-ordering/order-request")) {
      getOrderDetails(orderId).then((response) => {
        if (response?.status === "success") {
          dispatch(replaceOnlineOrder(response.data));
        } else {
          console.error("Failed to fetch order details:", response?.message);
        }
      }).catch((error) => {
        console.error("Error fetching order details:", error);
      });
    }
  }







  useEffect(() => {
    
    addEventHandler('new-online-notification', (data) => {
      // if(data?.shared_till_ids && data?.shared_till_ids?.includes(settings?.take_order?.device_id)) {
      if(true) {
        playIncomingSound();
        console.log("Received new-online-notification event:", data);
        if(data?.category == "online_notification") {

          if(data?.type == "online_order_placed") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success( data?.message || "New online order received");
            addOnlineOrdertoRequestList(data?.data?.online_order_id);
          }
          else if(data?.type == "online_order_accepted" || data?.type == "online_order_rejected") {
            toast.success( data?.message || `Order ${data?.data?.unique_id} ${data?.data?.status}`);
            // remove order with data?.data?.order_id if it exists in the list
            setNotifications((prev) =>
              prev.filter((item) => item?.data?.online_order_id !== data?.data?.online_order_id)
            );
            setNotifications((prev) => [data, ...prev]);
            updateOnlineOrderInRequestList(data?.data?.online_order_id);
            
          }
          else if(data?.type == "reservation_created") {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast.success( data?.message || "New online reservation received");
          }
          else if(data?.type == "reservation_accepted" || data?.type == "reservation_rejected") {
            toast.success( data?.message || `Order ${data?.data?.unique_id} ${data?.data?.status}`);
            // remove reservation with data?.data?.reservation_id if it exists in the list
            setNotifications((prev) =>
              prev.filter((item) => item?.data?.reservation_id !== data?.data?.reservation_id)
            );
            setNotifications((prev) => [data, ...prev]);
          }
          else if(data?.type == "reservation_deposit_paid") {
            toast.success( data?.message || `Reservation ${data?.data?.unique_id || ""} deposit paid`);
            setNotifications((prev) =>
              prev.filter((item) => item?.data?.reservation_id !== data?.data?.reservation_id)
            );
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);

            const reservationId = data?.data?.reservation_id || data?.reservation_id;
            if (reservationId) {
              getReservationById(reservationId)
                .then((res) => {
                  if (res?.status === "success" && res?.data?.reservation) {
                    dispatch(updateReservationState(res.data.reservation));
                  }
                })
                .catch((err) => console.error(err));
            }
          }
          else if(
            data?.type == "reservation_deposit_refunded" ||
            data?.type == "reservation_deposit_payment_cancelled" ||
            data?.type == "reservation_payment_cancelled"
          ) {
            toast.error( data?.message || `Reservation ${data?.data?.unique_id || ""} deposit refunded`);
            setNotifications((prev) =>
              prev.filter((item) => item?.data?.reservation_id !== data?.data?.reservation_id)
            );
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);

            const reservationId = data?.data?.reservation_id || data?.reservation_id;
            if (reservationId) {
              getReservationById(reservationId)
                .then((res) => {
                  if (res?.status === "success" && res?.data?.reservation) {
                    dispatch(updateReservationState(res.data.reservation));
                  }
                })
                .catch((err) => console.error(err));
            }
          }
          else {
            setNotifications((prev) => [data, ...prev]);
            setTotalUnread((prev) => prev + 1);
            toast(data?.message || data?.title || "New online notification received");
          }
        }
      }
    })

  }, [addEventHandler, tableStatus, tableConnected]);

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    setLoadingMore(true);
    
    const response = await getNotificationsApi(restaurant_id, {
      per_page: 5,
      page: nextPage,
      category : "online_notification", 
    });

    const list = response?.data?.notifications || [];
    const pagination = response?.data?.pagination || response?.pagination || {};

    setNotifications((prev) => [...prev, ...list]);
    
    // Check if there are more pages
    const currentPage = pagination.current_page || pagination.currentPage || nextPage;
    const lastPage = pagination.last_page || pagination.lastPage || 1;
    setHasMore(currentPage < lastPage);
    
    setLoadingMore(false);
  };

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!notificationsContainerRef.current) return;
    
    const container = notificationsContainerRef.current;
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    
    if (isNearBottom && hasMore && !loadingMore && !loading) {
      handleLoadMore();
    }
  }, [hasMore, loadingMore, loading]);

  useEffect(() => {
    const container = notificationsContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Auto-mark unread notifications as read after 3 seconds
  useEffect(() => {
    if(!open) return;
    // Clear any existing timeout
    if (autoMarkTimeoutRef.current) {
      clearTimeout(autoMarkTimeoutRef.current);
    }

    // Find unread notifications
    const unreadNotifications = notifications.filter((item) => !item?.is_read);

    if (unreadNotifications.length === 0) return;

    // Set timeout to mark them as read after 3 seconds
    autoMarkTimeoutRef.current = setTimeout(async () => {
      const unreadIds = unreadNotifications
        .map((item) => item.id)
        .filter(Boolean);

      if (unreadIds.length > 0) {
        try {
          const response = await updateNotificationsReadStatusApi(restaurant_id, {
            ids: unreadIds,
            is_read: true,
          });

          if (response?.status === "success") {
            setTotalUnread(response.data.total_unread || 0);
            setNotifications((prev) =>
              prev.map((item) =>
                unreadIds.includes(item.id) ? { ...item, is_read: true } : item
              )
            );
          }
        } catch (error) {
          console.error("Error marking notifications as read:", error);
        }
      }
    }, 3000); // 3 seconds

    return () => {
      if (autoMarkTimeoutRef.current) {
        clearTimeout(autoMarkTimeoutRef.current);
      }
    };
  }, [notifications, restaurant_id]);

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <span className="bg-cblack7/60 p-1.5 rounded text-cwhite2 cursor-pointer relative">
            <TbWorldDown size={24} />
            {(unreadCount > 0 || totalUnread > 0) && (
              <div className="absolute h-2 w-2 bg-color8 rounded-full top-2 right-2"></div>
            )}
          </span>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] h-[600px] bg-cblack3 -right-8 absolute overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-cwhite text-lg">Online Requests</div>
            <span className="text-sm text-color8">
              { totalUnread ?? unreadCount} Unread
            </span>
          </div>
          <Separator className="mb-4" />
          <div className="space-y-3 text-cwhite2 mt-3 h-[400px] overflow-auto " ref={notificationsContainerRef}>
            {loading &&
              skeletonRows.map((_, index) => (
                <div
                  key={`sk-${index}`}
                  className="flex items-center gap-4 rounded-md bg-cblack2/60 p-3 animate-pulse"
                >
                  <div className="h-10 w-10 rounded-full bg-cblack7/60" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-4/5 rounded bg-cblack7/60" />
                    <div className="h-3 w-2/5 rounded bg-cblack7/60" />
                  </div>
                </div>
              ))}

            {!loading && notifications.length === 0 && (
              <div className="text-sm text-cwhite3 text-center py-6">
                No notifications found.
              </div>
            )}

            {!loading &&
              notifications.map((item) => {
                const orderData = item?.data || {};
                const isReservation = item?.type === "reservation_created" || item?.type === "reservation_accepted" || item?.type === "reservation_rejected" || item?.type === "reservation_deposit_paid";
                
                // For orders
                const orderId = orderData?.unique_id || `#${item?.id ?? ""}`;
                const customerName = orderData?.customer_full_name || orderData?.customer?.full_name || orderData?.customer_name || "Unknown";
                const orderType = orderData?.order_type || "Order";
                const itemsCount = orderData?.items_count || orderData?.total_quantity || 0;
                const totalAmount = orderData?.total_amount || 0;
                
                // For reservations
                const reservationId = orderData?.unique_id || `#${item?.id ?? ""}`;
                const reservationCustomer = orderData?.customer_full_name || orderData?.customer?.full_name || orderData?.customer_name || "Unknown";
                const guestCount = orderData?.guest || 0;
                const reservationDateTime = orderData?.date_time || "N/A";

                const getOrderTypeColor = (type) => {
                  switch (type?.toLowerCase()) {
                    case "pickup":
                      return "bg-blue-600/20 text-blue-400";
                    case "delivery":
                      return "bg-purple-600/20 text-purple-400";
                    default:
                      return "bg-orange-600/20 text-orange-400";
                  }
                };

                if (isReservation) {
                  return (
                    <div
                      key={item?.id ?? reservationId}
                      className="bg-cblack2 rounded-lg p-4 border border-cwhite/10 hover:border-cyellow/30 transition"
                    >
                      {/* Header: Reservation ID and Status */}
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-cwhite font-semibold text-sm">
                            Reservation ID : {reservationId}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-green-400"> 
                            {reservationDateTime}
                          </span>
                        </div>
                      </div>

                      {/* Reservation Type Badge */}
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-xs px-2 py-1 rounded bg-blue-600/20 text-blue-400">
                          Reservation
                        </span>
                        <span className="text-xs text-cyan-400 font-medium">Online Reservation</span>
                      </div>

                      {/* Customer Info */}
                      <div className="mb-3">
                        <p className="text-cwhite text-sm">Customer Name: {reservationCustomer}</p>
                      </div>

                      {/* Guest Count and Date/Time */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <Users size={16} className="text-cwhite3" />
                            <span className="text-cwhite text-sm">{guestCount} Guests</span>
                          </div>
                        </div>
                        <div className="text-sm text-cwhite3">
                          {reservationDateTime}
                        </div>
                      </div>

                      {/* View Details Link */}
                      <div className="mb-4">
                        <Link
                          to={`/reservation/rq-details/${orderData?.reservation_id}`}
                          className="text-cyellow text-sm hover:underline"
                        >
                          View Reservation Details
                        </Link>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        {
                          orderData?.status != "rejected" &&  orderData?.status != "paid" &&
                          (orderData?.status === "accepted" ?
                          <span className="flex-1 text-center bg-green-600/20 text-green-400 font-semibold py-2 rounded transition">
                            Accepted
                          </span>:
                            <button
                              onClick={() =>
                                handleShowConfirm(
                                  "accept",
                              orderData?.reservation_id,
                              reservationId,
                              "reservation"
                            )
                          }
                          className="flex-1 bg-green-500 hover:bg-green-600 text-cwhite font-semibold py-2 rounded transition"
                        >
                          Accept
                        </button>)
                       }
                        
                        {
                          orderData?.status != "accepted" && orderData?.status != "paid" &&
                          (orderData?.status === "rejected" ?
                          <span className="flex-1 text-center bg-red-600/20 text-red-400 font-semibold py-2 rounded transition">
                            Rejected
                          </span>:
                        
                          <button
                            onClick={() =>
                              handleShowConfirm(
                                "reject",
                                orderData?.reservation_id,
                                reservationId,
                                "reservation"
                              )
                            }
                            className="flex-1 bg-red-600 hover:bg-red-700 text-cwhite font-semibold py-2 rounded transition"
                          >
                            Reject
                          </button>)
                      }
                      {
                        orderData?.status === "paid" && 
                        <span className="flex-1 text-center bg-green-600/20 text-green-400 font-semibold py-2 rounded transition">
                            Paid
                          </span>
                      }
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={item?.id ?? orderId}
                    className="bg-cblack2 rounded-lg p-4 border border-cwhite/10 hover:border-cyellow/30 transition"
                  >
                    {/* Header: Order ID and Time */}
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="text-cwhite font-semibold text-sm">
                          Order Id : {orderId}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-green-400">{new Date(item?.created_at).toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Order Type Badge and Status */}
                    <div className="flex justify-between items-start mb-3">
                      <span className={`text-xs px-2 py-1 rounded ${getOrderTypeColor(orderType)}`}>
                        {orderType.charAt(0).toUpperCase() + orderType.slice(1)}
                      </span>
                      <span className="text-xs text-red-400 font-medium">Online order</span>
                    </div>

                    {/* Customer Info */}
                    <div className="mb-3">
                      <p className="text-cwhite text-sm">Customer Name: {customerName}</p>
                    </div>

                    {/* Items Count */}
                    <div className="flex items-center gap-2 mb-4">
                      <Users size={16} className="text-cwhite3" />
                      <span className="text-cwhite text-sm">{itemsCount}</span>
                    </div>

                    {/* Price and View Details */}
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-cyellow font-bold text-lg">
                        £ {totalAmount}
                      </span>
                      <Link
                        to={`/onlineordering/website-ordering/order-request/${orderData?.online_order_id ?? ""}`}
                        className="text-cyellow text-sm hover:underline"
                      >
                        View Details
                      </Link>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      {
                        orderData?.status != "rejected" &&
                        (orderData?.status === "accepted" ? 
                          <span className="flex-1 text-center bg-green-600/20 text-green-400 font-semibold py-2 rounded transition">
                            Accepted  
                          </span>
                        : 
                          <button
                          onClick={() =>
                            handleShowConfirm(
                              "accept",
                              orderData?.online_order_id,
                              orderId,
                              "order"
                            )
                          }
                          className="flex-1 bg-green-500 hover:bg-green-600 text-cwhite font-semibold py-2 rounded transition"
                        >
                          Accept
                        </button>)
                      }
                        {
                          orderData?.status != "accepted" &&
                          (orderData?.status === "rejected" ? 
                          <span className="flex-1 text-center bg-red-600/20 text-red-400 font-semibold py-2 rounded transition">
                            Rejected  
                          </span>
                          :
                          <button
                            onClick={() =>
                              handleShowConfirm(
                                "reject",
                                orderData?.online_order_id,
                                orderId,
                                "order"
                              )
                            }
                            className="flex-1 bg-red-600 hover:bg-red-700 text-cwhite font-semibold py-2 rounded transition"
                          >
                            Reject
                          </button>)
                        }

                    </div>
                  </div>
                );
              })}

          {loadingMore &&
            skeletonRows.map((_, index) => (
              <div
                key={`sk-loading-${index}`}
                className="bg-gray-700 rounded-md p-5 flex flex-col gap-3 animate-pulse"
              >
                <div className="flex justify-between items-start">
                  <div className="w-2/3">
                    <div className="h-5 bg-gray-600 rounded w-32 mb-2"></div>
                    <div className="h-4 bg-gray-600 rounded w-48"></div>
                  </div>
                  <div className="w-24">
                    <div className="h-5 bg-gray-600 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-600 rounded w-3/4"></div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-7 w-12 bg-gray-600 rounded" />
                    <div className="h-7 w-12 bg-gray-600 rounded" />
                  </div>
                  <div className="h-8 w-20 bg-gray-600 rounded" />
                </div>
              </div>
            ))}
        </div>

        {/* Load More Button */}
        {hasMore && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="px-2 py-.5 bg-cyellow/10 text-cyellow hover:bg-cyellow/20 rounded border border-cyellow/30 disabled:opacity-50 transition"
            >
              {loadingMore ? "Loading..." : "Load More"}
            </button>
          </div>
        )}

        <div className=" mt-6 flex justify-center">
          <Link to={"/backoffice/online-notification"} className="w-full">
            <Button className="w-full bg-cyellow/10 text-cyellow hover:bg-cyellow/20 border border-cyellow/30">
              View all online requests
            </Button>
          </Link>
        </div>
      </PopoverContent>
      </Popover>

      {/* Independent Confirmation Modal - Rendered outside Popover */}
      {orderConfirmModal.isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleOrderCancelAction}
          />
          <div
            role="dialog"
            aria-modal="true"
            className="relative bg-cblack3 border border-cwhite/10 rounded-lg shadow-lg max-w-md w-full mx-4 p-6"
          >
            <h3 className="text-lg font-semibold text-cwhite mb-2">
              {orderConfirmModal.action === "accept"
                ? "Accept Order?"
                : "Reject Order?"}
            </h3>
            <p className="text-sm text-cwhite3 mb-4">
              Are you sure you want to{" "}
              <span className="font-semibold">
                {orderConfirmModal.action === "accept" ? "accept" : "reject"}
              </span>{" "}
              order <span className="text-cyellow">{orderConfirmModal.orderName}</span>?
              {orderConfirmModal.action === "reject" && (
                <span className="block mt-2 text-red-400">
                  This action cannot be undone.
                </span>
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleOrderCancelAction}
                disabled={orderConfirmModal.isLoading}
                className="px-4 py-2 rounded border border-cwhite/20 bg-transparent text-cwhite hover:bg-cwhite/10 disabled:opacity-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAcceptOrderAction}
                disabled={orderConfirmModal.isLoading}
                className={`px-4 py-2 rounded text-cwhite font-semibold disabled:opacity-50 transition ${
                  orderConfirmModal.action === "accept"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {orderConfirmModal.isLoading ? "Loading..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Independent Confirmation Modal - Rendered outside Popover */}
      {reservationConfirmModal.isOpen && reservationConfirmModal.action === "accept" && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleReservationCancelAction}
          />
          <div
            role="dialog"
            aria-modal="true"
            className="relative bg-cblack3 border border-cwhite/10 rounded-lg shadow-lg max-w-md w-full mx-4 p-6"
          >
            <h3 className="text-lg font-semibold text-cwhite mb-2">
              Accept Reservation?
            </h3>
            <p className="text-sm text-cwhite3 mb-4">
              Are you sure you want to{" "}
              <span className="font-semibold">
                accept
              </span>{" "}
              reservation <span className="text-cyellow">{reservationConfirmModal.reservationName}</span>?
              {reservationConfirmModal.action === "reject" && (
                <span className="block mt-2 text-red-400">
                  This action cannot be undone.
                </span>
              )}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleReservationCancelAction}
                disabled={reservationConfirmModal.isLoading}
                className="px-4 py-2 rounded border border-cwhite/20 bg-transparent text-cwhite hover:bg-cwhite/10 disabled:opacity-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAcceptReservationAction}
                disabled={reservationConfirmModal.isLoading}
                className={`px-4 py-2 rounded text-cwhite font-semibold disabled:opacity-50 transition bg-green-600 hover:bg-green-700
                }`}
              >
                {reservationConfirmModal.isLoading ? "Loading..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReservationRejectDialog
        isOpen={reservationConfirmModal.isOpen && reservationConfirmModal.action === "reject"}
        onClose={() => setReservationConfirmModal({ ...reservationConfirmModal, isOpen: false })}
        reservation_id={reservationConfirmModal.reservationId}
      />
    </>
  );
}

export default OnlineOrderNotification;
